import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import Select from 'react-select';
import Layout from "../../Component/Layout"
import { toast } from "react-toastify";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CharacteristicsModal from '../../Component/CharacteristicsModal ';
import PropTypes from 'prop-types';
import { ORDER_API } from "../../apiConfig";
import { MASTER_API } from "../../apiConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { FileText } from 'lucide-react';
import { authHeaders } from "../../utils/authHeaders";

// --- Shared dropdown option/menu styling (matches Sales Order dropdown look) ---
const dropdownOptionStyle = {
    control: (base) => ({
        ...base,
        minHeight: 32,
        height: 32,
        fontSize: '13px',
        borderColor: '#dee2e6',
        boxShadow: 'none',
        '&:hover': { borderColor: '#b3b3b3' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: '32px', display: 'flex', alignItems: 'center' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({ ...base, fontSize: '13px', zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : 'white',
        color: state.isSelected ? 'white' : '#334155',
        cursor: 'pointer',
        fontSize: '13px'
    })
};


// Helpers for "BATCH - qty" strings. The qty suffix is what the backend deducts
// from stock, so a batch can be picked PARTIALLY (qty < full batch stock).
const round3 = (n) => Math.round(n * 1000) / 1000;
const batchNameOf = (str) => {
    if (typeof str !== 'string') return str;
    const i = str.lastIndexOf(' - ');
    return i === -1 ? str : str.substring(0, i).trim();
};
const batchQtyOf = (str) => {
    if (typeof str !== 'string') return 0;
    const m = str.match(/ - (\d+(?:\.\d+)?)\s*$/);
    return m ? parseFloat(m[1]) : 0;
};

const MultiSelectDropdown = ({ options, selectedValues, onChange, rowKey, maxQty, deliveredQty = 0, rangeMin, rangeMax, soId, productCode }) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState({});   // { batch_number: "qty string" } -- only ticked batches are keys
    const [error, setError] = useState("");

    const hasLimit = Number.isFinite(maxQty);

    // Same batch_number can appear on several stock rows (e.g. split across storage
    // locations). Delivery deducts by batch_number, so merge them into one pickable row.
    const batches = useMemo(() => {
        const map = new Map();
        (options || []).forEach((o) => {
            const q = parseFloat(o.qty) || 0;
            const ex = map.get(o.batch_number);
            if (ex) {
                ex.qty = round3(ex.qty + q);
                if (o.storage_location && !ex.locations.includes(o.storage_location)) ex.locations.push(o.storage_location);
            } else {
                map.set(o.batch_number, {
                    batch_number: o.batch_number,
                    qty: q,
                    locations: o.storage_location ? [o.storage_location] : []
                });
            }
        });
        return Array.from(map.values());
    }, [options]);

    const draftTotal = round3(Object.values(draft).reduce((s, v) => s + (parseFloat(v) || 0), 0));
    // Range for THIS SO line item: previously delivered + newly selected must stay within it.
    const overRange = hasLimit && draftTotal > maxQty + 0.001;
    const remainingRange = hasLimit ? round3(maxQty - draftTotal) : null;

    const openPanel = () => {
        const init = {};
        selectedValues.forEach((v) => { init[batchNameOf(v)] = String(batchQtyOf(v) || ""); });
        setDraft(init);
        setError("");
        setOpen(true);
    };

    const toggleBatch = (b) => {
        setDraft((prev) => {
            const next = { ...prev };
            if (b.batch_number in next) {
                delete next[b.batch_number];
            } else {
                // Suggest what's still needed, but the user is free to change it.
                const used = Object.values(prev).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                const remaining = hasLimit ? Math.max(0, round3(maxQty - used)) : b.qty;
                next[b.batch_number] = String(round3(Math.min(b.qty, remaining)) || "");
            }
            return next;
        });
        setError("");
    };

    const changeQty = (batchNo, val) => {
        setDraft((prev) => ({ ...prev, [batchNo]: val }));
        setError("");
    };

    const handleConfirm = () => {
        const result = [];
        for (const b of batches) {
            if (!(b.batch_number in draft)) continue;
            const q = parseFloat(draft[b.batch_number]);
            if (isNaN(q) || q <= 0) {
                setError(`Enter a qty greater than 0 for batch ${b.batch_number}, or untick it.`);
                return;
            }
            if (q > b.qty + 0.0001) {
                setError(`Batch ${b.batch_number} only has ${b.qty} available.`);
                return;
            }
            result.push(`${b.batch_number} - ${round3(q)}`);
        }
        if (hasLimit && draftTotal > maxQty + 0.001) {
            setError(`Delivered (${round3(deliveredQty)}) + selected (${draftTotal}) = ${round3(deliveredQty + draftTotal)} crosses the range ${rangeMin} - ${rangeMax} for SO ${soId}, item ${productCode}.`);
            return;
        }
        onChange(result, rowKey);
        setOpen(false);
    };

    const selectedTotal = selectedValues.reduce((s, v) => s + batchQtyOf(v), 0);
    const label = selectedValues.length === 0
        ? "Select Batches"
        : selectedValues.map((v) => batchQtyOf(v) > 0 ? `${batchNameOf(v)}(${batchQtyOf(v)})` : batchNameOf(v)).join(", ");

    const th = { padding: "6px 8px", fontWeight: 600, color: "#64748b", borderBottom: "1px solid #e2e8f0" };

    return (
        <div style={{ position: 'relative', minWidth: '180px' }}>
            <div
                className="form-control form-control-sm"
                onClick={openPanel}
                title={label + (selectedValues.length ? `  |  Total: ${round3(selectedTotal)}` : "")}
                style={{
                    cursor: 'pointer', height: '32px', fontSize: '12px', whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center',
                    background: selectedValues.length ? '#eff6ff' : '#fff',
                    color: selectedValues.length ? '#1d4ed8' : '#64748b'
                }}
            >
                {label}
            </div>

            {open && ReactDOM.createPortal(
                <div
                    style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }}
                    onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
                >
                    <div style={{ background: '#fff', borderRadius: 8, width: 560, maxWidth: '95vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Select Batches</div>
                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                    SO <b>{soId}</b> &nbsp;|&nbsp; Item <b>{productCode}</b>
                                </div>
                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                    {rangeMax !== undefined && <>Range: <b>{rangeMin} - {rangeMax}</b> &nbsp;|&nbsp; </>}
                                    Already delivered: <b>{round3(deliveredQty)}</b> &nbsp;|&nbsp;{' '}
                                    Selected now: <b style={{ color: overRange ? '#dc2626' : '#16a34a' }}>{draftTotal}</b>
                                    {hasLimit && <> &nbsp;|&nbsp; Total: <b style={{ color: overRange ? '#dc2626' : '#16a34a' }}>{round3(deliveredQty + draftTotal)}</b> / {rangeMax}</>}
                                </div>
                            </div>
                            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer' }}>×</button>
                        </div>

                        <div style={{ overflowY: 'auto', flex: 1, padding: '8px 16px' }}>
                            {batches.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8', fontStyle: 'italic' }}>No matching batches</div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead>
                                        <tr style={{ background: '#f1f5f9' }}>
                                            <th style={{ ...th, textAlign: 'left' }}></th>
                                            <th style={{ ...th, textAlign: 'left' }}>Batch No</th>
                                            <th style={{ ...th, textAlign: 'right' }}>Available</th>
                                            <th style={{ ...th, textAlign: 'left' }}>Storage Location</th>
                                            <th style={{ ...th, textAlign: 'right' }}>Use Qty</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {batches.map((b, i) => {
                                            const checked = b.batch_number in draft;
                                            return (
                                                <tr key={b.batch_number} style={{ background: checked ? '#eff6ff' : (i % 2 === 0 ? '#fff' : '#fafbfc'), borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '6px 8px' }}>
                                                        <input type="checkbox" checked={checked} onChange={() => toggleBatch(b)} style={{ accentColor: '#2563eb', width: 14, height: 14 }} />
                                                    </td>
                                                    <td style={{ padding: '6px 8px', fontWeight: 600, color: '#1e293b' }}>{b.batch_number}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>{b.qty}</td>
                                                    <td style={{ padding: '6px 8px', color: '#475569' }}>{b.locations.join(', ') || '—'}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                                        {checked ? (
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                max={b.qty}
                                                                value={draft[b.batch_number] ?? ""}
                                                                onChange={(e) => changeQty(b.batch_number, e.target.value)}
                                                                style={{ width: 80, padding: '2px 5px', fontSize: 12, border: '1px solid #93c5fd', borderRadius: 4, height: 26, textAlign: 'right', outline: 'none' }}
                                                            />
                                                        ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {!error && overRange && <div style={{ padding: '6px 16px', background: '#fef2f2', color: '#dc2626', fontSize: 12, borderTop: '1px solid #fecaca' }}>Delivered ({round3(deliveredQty)}) + selected ({draftTotal}) = {round3(deliveredQty + draftTotal)} crosses the range {rangeMin} - {rangeMax} for SO {soId}, item {productCode}.</div>}
                        {error && <div style={{ padding: '6px 16px', background: '#fef2f2', color: '#dc2626', fontSize: 12, borderTop: '1px solid #fecaca' }}>{error}</div>}
                        <div style={{ padding: '10px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#f8fafc' }}>
                            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 5, fontSize: 12, cursor: 'pointer', color: '#374151' }}>Cancel</button>
                            <button onClick={handleConfirm} disabled={overRange} style={{ padding: '6px 16px', background: overRange ? '#94a3b8' : '#1e293b', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: overRange ? 'not-allowed' : 'pointer' }}>Confirm</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

MultiSelectDropdown.propTypes = {
    options: PropTypes.array.isRequired,
    selectedValues: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    rowKey: PropTypes.string.isRequired,
    maxQty: PropTypes.number,
    deliveredQty: PropTypes.number,
    rangeMin: PropTypes.number,
    rangeMax: PropTypes.number,
    soId: PropTypes.string,
    productCode: PropTypes.string
};

const USER_MANUAL_SCOPE = {
    moduleName: "Logistics",
    process: "Outbound",
    subProcess: "Outbound Delivery"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

function OutboundDelivery() {
    const location = useLocation();
    const navigate = useNavigate();
    const [salesOrganization, setSalesOrganization] = useState("");
    const [customerSegment, setCustomerSegment] = useState("");
    const [shippingFrom, setShippingFrom] = useState("");
    const [dChannel, setDChannel] = useState("");
    const [soId, setSoId] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState("");
    const [rfqSummary, setRfqSummary] = useState([]);
    const [postedSummary, setPostedSummary] = useState([]);
    const [mainTab, setMainTab] = useState("active"); // "active" | "posted"
    const [selectedRows, setSelectedRows] = useState([]);
    const [modalData, setModalData] = useState([]);
    const [changeLogs] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const [manualFile, setManualFile] = useState(null);
    const [modalType, setModalType] = useState('');
    const [viewSource, setViewSource] = useState(null); // "active" | "posted" — controls whether Save is allowed
    const [activeTab, setActiveTab] = useState("product");
    const [expandedProductSoId, setExpandedProductSoId] = useState(null);
    const [expandedDocsSoId, setExpandedDocsSoId] = useState(null);
    const [showDeliveryFormPreview, setShowDeliveryFormPreview] = useState(null); // od_id currently previewed inline, or null
    const [batchOptions, setBatchOptions] = useState({});
    const [selectedBatches, setSelectedBatches] = useState({});
    const [selectedCharData, setSelectedCharData] = useState([]);
    const [showCharModal, setShowCharModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [salesOrgOptions, setSalesOrgOptions] = useState([]);
    const [customerSegmentOptions, setCustomerSegmentOptions] = useState([]);
    const [distributionChannelOptions, setDistributionChannelOptions] = useState([]);
    const [shippingFromOptions, setShippingFromOptions] = useState([]);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                const res = await fetch(`${ORDER_API}/outbound-delivery/summary/created`);
                const data = await res.json();
                setRfqSummary(Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []));
            } catch (err) { console.error('Failed to load RFQ summary:', err); }
        };
        fetchSummary();
    }, []);

    useEffect(() => {
        if (mainTab !== "posted") return;
        const fetchPostedSummary = async () => {
            try {
                const res = await fetch(`${ORDER_API}/outbound-delivery/summary/posted`);
                const data = await res.json();
                setPostedSummary(Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []));
            } catch (err) { console.error('Failed to load posted deliveries:', err); }
        };
        fetchPostedSummary();
    }, [mainTab]);

    useEffect(() => {

        const fetchDropdowns = async () => {

            try {

                const [
                    salesOrgRes,
                    customerSegmentRes,
                    distributionRes,
                    shippingRes
                ] = await Promise.all([

                    fetch(`${MASTER_API}/configuration/sales-organization`),
                    fetch(`${MASTER_API}/configuration/customer-segment`),
                    fetch(`${MASTER_API}/configuration/distribution-channel`),
                    fetch(`${MASTER_API}/configuration/shipping-from`)

                ]);

                const salesOrgData = await salesOrgRes.json();
                const customerSegmentData = await customerSegmentRes.json();
                const distributionData = await distributionRes.json();
                const shippingData = await shippingRes.json();

                setSalesOrgOptions(salesOrgData.data || salesOrgData);
                setCustomerSegmentOptions(customerSegmentData.data || customerSegmentData);
                setDistributionChannelOptions(distributionData.data || distributionData);
                setShippingFromOptions(shippingData.data || shippingData);

            } catch (err) {

                console.error("Dropdown fetch error", err);

            }

        };

        fetchDropdowns();

    }, []);

    const dropdownOptions = useMemo(() => {
        const getUnique = (key) => [...new Set((Array.isArray(rfqSummary) ? rfqSummary : []).map(item => item[key]).filter(Boolean))];
        return {
            salesOrgs: getUnique('sales_organization'),
            customerSegments: getUnique('customer_segment'),
            shippingFroms: getUnique('shipping_from'),
            channels: getUnique('distribution_channel'),
            customers: getUnique('customer'),
            soIds: getUnique('so_id'),
        };
    }, [rfqSummary]);

    const filteredData = useMemo(() => {
        return rfqSummary.filter((item) => {
            return (
                (salesOrganization === "" || item.sales_organization === salesOrganization) &&
                (customerSegment === "" || item.customer_segment === customerSegment) &&
                (shippingFrom === "" || item.shipping_from === shippingFrom) &&
                (dChannel === "" || item.distribution_channel === dChannel) &&
                (selectedCustomer === "" || item.customer === selectedCustomer) &&
                (soId === "" || item.so_id === soId)
            );
        });
    }, [rfqSummary, salesOrganization, customerSegment,
        shippingFrom, dChannel, selectedCustomer, soId]);

    const filteredPostedData = useMemo(() => {
        return postedSummary.filter((item) => {
            return (
                (salesOrganization === "" || item.sales_organization === salesOrganization) &&
                (customerSegment === "" || item.customer_segment === customerSegment) &&
                (dChannel === "" || item.distribution_channel === dChannel) &&
                (selectedCustomer === "" || item.customer === selectedCustomer) &&
                (soId === "" || item.so_id === soId)
            );
        });
    }, [postedSummary, salesOrganization, customerSegment, dChannel, selectedCustomer, soId]);

    useEffect(() => {
        if (showModal) {
            setActiveTab("product");
            setExpandedProductSoId(null);
            setExpandedDocsSoId(null);
        }
    }, [showModal, modalData]);

    useEffect(() => {
        if (location.state?.openOdId) {
            const odId = location.state.openOdId;
            handlePostedViewClick(odId);
            window.history.replaceState({}, document.title);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const batchOptionsRef = useRef(batchOptions);
    useEffect(() => {
        batchOptionsRef.current = batchOptions;
    }, [batchOptions]);

    useEffect(() => {
        if (showModal && activeTab === 'product' && modalData.length > 0 && viewSource !== 'posted') {
            const fetchBatches = async () => {
                const uniqueCodes = new Set();
                modalData.forEach(item => {
                    item.productDetails?.forEach(p => { if (p.product_code) uniqueCodes.add(p.product_code); });
                });

                const newOptions = { ...batchOptionsRef.current };
                let hasNewUpdates = false;

                await Promise.all(Array.from(uniqueCodes).map(async (code) => {
                    if (!newOptions[code]) {
                        try {
                            const res = await fetch(
                                `${ORDER_API}/outbound-delivery/batches/${code}`
                            );
                            const data = await res.json();

                            console.log("PRODUCT CODE:", code);
                            console.log("API RESPONSE:", data);

                            newOptions[code] = data;
                            hasNewUpdates = true;
                        } catch (err) { console.error(`Error loading batches for ${code}`, err); }
                    }
                }));
                if (hasNewUpdates) setBatchOptions(newOptions);
            };
            fetchBatches();
        }
    }, [showModal, modalType, activeTab, modalData, viewSource]);

    const handleShowCharacteristics = (charData) => {
        let parsedData = [];
        try { parsedData = typeof charData === 'string' ? JSON.parse(charData) : charData; } catch (e) { parsedData = []; }
        setSelectedCharData(parsedData || []);
        setShowCharModal(true);
    };

    const handleToggle = (tab, sId) => {
        if (tab === "product") setExpandedProductSoId(prev => prev === sId ? null : sId);
        if (tab === "docs") setExpandedDocsSoId(prev => prev === sId ? null : sId);
    };

    const renderProductContent = (item) => (
        <div style={{ ...styles.tableContainer, overflowX: 'auto', marginTop: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', width: '100%' }}>
            <table className="table table-hover mb-0" style={{ ...styles.erpTable, width: 'max-content', minWidth: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={{ ...styles.erpTh, textAlign: "center", width: '50px' }}>Item</th>
                        <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Product Code</th>
                        <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Description</th>
                        <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Class</th>
                        <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>UOM</th>
                        <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Quantity</th>
                        {viewSource !== 'posted' && <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Open Qty</th>}
                        {viewSource !== 'posted' && <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Range</th>}
                        <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Req. Date</th>
                        <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Del. Qty</th>
                        <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', minWidth: '180px' }}>Batch Number</th>
                        <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Storage Location</th>
                    </tr>
                </thead>
                <tbody>
                    {item.productDetails?.length === 0 ? (
                        <tr><td colSpan={viewSource === 'posted' ? 10 : 12} style={{ ...styles.erpTd, textAlign: "center", padding: '20px' }}>No Products Found</td></tr>
                    ) : (
                        item.productDetails?.map((prod, idx) => {
                            const rowKey = `${item.so_id}|${prod.product_code}|${idx}`;

                            // THE FIX: Properly handle '||' separated batches coming from DB
                            let parsedDBBatches = [];
                            if (prod.batch_number) {
                                parsedDBBatches = prod.batch_number.split('||').map(s => s.trim()).filter(Boolean);
                            }

                            const currentSelection = Array.isArray(selectedBatches[rowKey])
                                ? selectedBatches[rowKey]
                                : (selectedBatches[rowKey] ? [selectedBatches[rowKey]] : parsedDBBatches);

                            const calculatedQty = currentSelection.reduce((sum, batchStr) => {
                                if (typeof batchStr === 'string') {
                                    // Only treat trailing " - <number>" as an appended qty suffix
                                    // (added when a batch is freshly picked from the dropdown).
                                    // Raw batch codes from the DB (e.g. "PRD0001-1") have no such
                                    // suffix and must NOT be mistaken for one.
                                    const match = batchStr.match(/ - (\d+(?:\.\d+)?)\s*$/);
                                    if (match) {
                                        const q = parseFloat(match[1]);
                                        return sum + (isNaN(q) ? 0 : q);
                                    }
                                }
                                return sum;
                            }, 0);
                            const displayDeliveryQty = calculatedQty > 0 ? calculatedQty : prod.delivery_qty;

                            const rawOptions = batchOptions[prod.product_code];
                            const availableBatches = Array.isArray(rawOptions) ? rawOptions : [];

                            // Exclude batches already picked on OTHER line items with the same product code
                            // (not yet saved), so the same batch can't be double-assigned.
                            const batchesUsedElsewhere = new Set();
                            Object.keys(selectedBatches).forEach((otherKey) => {
                                if (otherKey === rowKey) return;
                                const [, otherProductCode] = otherKey.split('|');
                                if (otherProductCode !== prod.product_code) return;
                                const otherSelection = Array.isArray(selectedBatches[otherKey])
                                    ? selectedBatches[otherKey]
                                    : (selectedBatches[otherKey] ? [selectedBatches[otherKey]] : []);
                                otherSelection.forEach((val) => {
                                    if (typeof val === 'string' && val.includes(' - ')) {
                                        batchesUsedElsewhere.add(val.split(' - ')[0].trim());
                                    }
                                });
                            });

                            const filteredOptions = availableBatches.filter(
                                (opt) => !batchesUsedElsewhere.has(opt.batch_number)
                            );

                            // Max deliverable now = range max of this SO line (e.g. 980 - 1020)
                            // minus what's already delivered. Computed server-side.
                            const maxDeliveryQty = Number.isFinite(parseFloat(prod.max_delivery_qty))
                                ? parseFloat(prod.max_delivery_qty)
                                : (parseFloat(prod.open_qty) || 0);

                            const selectedBins = currentSelection.map(sel => {
                                const match = availableBatches.find(opt => opt.batch_number === batchNameOf(sel));
                                return match ? match.storage_location : null;
                            }).filter(Boolean);
                            const displayBin = [...new Set(selectedBins)].join(", ");

                            return (
                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ ...styles.erpTd, textAlign: "center" }}>{idx + 1}</td>
                                    <td style={styles.erpTd}>{prod.product_code}</td>
                                    <td style={styles.erpTd}>{prod.description}</td>
                                    <td style={styles.erpTd}>
                                        {prod.product_class}
                                        {prod.characteristics && (
                                            <span
                                                style={{ cursor: 'pointer', marginLeft: '8px', color: '#111111', fontSize: '12px' }}
                                                onClick={() => handleShowCharacteristics(prod.characteristics)}
                                                title="View Characteristics"
                                            >
                                                ▶
                                            </span>
                                        )}
                                    </td>
                                    <td style={styles.erpTd}>{prod.uom}</td>
                                    <td style={styles.erpTd}>{prod.quantity}</td>
                                    {viewSource !== 'posted' && (
                                        <td style={{ ...styles.erpTd, fontWeight: 600 }}>
                                            {prod.open_qty !== undefined && prod.open_qty !== null ? round3(parseFloat(prod.open_qty) || 0) : "-"}
                                        </td>
                                    )}
                                    {viewSource !== 'posted' && (
                                        <td style={{ ...styles.erpTd, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                            {prod.range_min !== undefined ? `${round3(prod.range_min)} - ${round3(prod.range_max)}` : '-'}
                                        </td>
                                    )}
                                    <td style={styles.erpTd}>
                                        {prod.requirement_date ? (() => {
                                            const d = new Date(prod.requirement_date);
                                            const day = String(d.getDate()).padStart(2, '0');
                                            const month = String(d.getMonth() + 1).padStart(2, '0');
                                            return `${day}/${month}/${d.getFullYear()}`;
                                        })() : "N/A"}
                                    </td>
                                    <td style={styles.erpTd}>{displayDeliveryQty}</td>

                                    {viewSource === 'posted' ? (
                                        <td style={styles.erpTd}>{parsedDBBatches.length > 0 ? parsedDBBatches.join(', ') : (prod.batch_number || '-')}</td>
                                    ) : (
                                        /* ALWAYS Render MultiSelect to allow changing batches before save */
                                        <td style={styles.erpTd}>
                                            <MultiSelectDropdown
                                                options={filteredOptions}
                                                selectedValues={currentSelection}
                                                rowKey={rowKey}
                                                maxQty={maxDeliveryQty}
                                                deliveredQty={parseFloat(prod.delivered_qty) || 0}
                                                rangeMin={Number.isFinite(parseFloat(prod.range_min)) ? parseFloat(prod.range_min) : undefined}
                                                rangeMax={Number.isFinite(parseFloat(prod.range_max)) ? parseFloat(prod.range_max) : undefined}
                                                soId={item.so_id}
                                                productCode={prod.product_code}
                                                onChange={(newValues, key) => {
                                                    const totalBatchQty = newValues.reduce((sum, batchStr) => {
                                                        if (typeof batchStr === 'string') {
                                                            const match = batchStr.match(/ - (\d+(?:\.\d+)?)\s*$/);
                                                            if (match) {
                                                                const q = parseFloat(match[1]);
                                                                return sum + (isNaN(q) ? 0 : q);
                                                            }
                                                        }
                                                        return sum;
                                                    }, 0);

                                                    // Safety net only -- the dropdown already clamps the last
                                                    // batch so the total never exceeds maxDeliveryQty.
                                                    if (totalBatchQty > maxDeliveryQty + 0.001) {
                                                        toast.warning(`SO ${item.so_id} / ${prod.product_code}: selected qty (${totalBatchQty}) crosses the range ${prod.range_min} - ${prod.range_max} (already delivered ${prod.delivered_qty || 0})`);
                                                        return;
                                                    }
                                                    setSelectedBatches(prev => ({ ...prev, [key]: newValues }));
                                                }}
                                            />
                                        </td>
                                    )}

                                    <td style={styles.erpTd}>{viewSource === 'posted' ? (prod.storage_bin || "-") : (displayBin || "-")}</td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );

    // Delivery Form (challan) is never stored — it's rendered live from the OD
    // record every time it's viewed/downloaded/printed, so there's nothing to
    // keep in sync. Same View/Download/Print card pattern as the Order
    // Confirmation doc in Sales Order: View opens an inline iframe modal
    // (stays on this screen — no new tab), Download/Print use a hidden
    // off-screen iframe that triggers the browser print/save dialog itself.
    const deliveryFormUrl = (od_id, mode) =>
        `/cable/logistics/outbound-delivery-form/${od_id}${mode ? `?${mode}=true` : ""}`;

    const handleDownloadDeliveryForm = (od_id) => {
        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.top = "0";
        iframe.style.left = "-10000px";
        iframe.style.width = "1050px";
        iframe.style.height = "2000px";
        iframe.style.border = "none";
        iframe.style.visibility = "hidden";
        iframe.src = deliveryFormUrl(od_id, "download");
        document.body.appendChild(iframe);
        setTimeout(() => { document.body.removeChild(iframe); }, 10000);
    };

    const handlePrintDeliveryForm = (od_id) => {
        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.top = "0";
        iframe.style.left = "-10000px";
        iframe.style.width = "1050px";
        iframe.style.height = "2000px";
        iframe.style.border = "none";
        iframe.style.visibility = "hidden";
        iframe.src = deliveryFormUrl(od_id, "print");
        document.body.appendChild(iframe);
        setTimeout(() => { document.body.removeChild(iframe); }, 10000);
    };

    const renderDocsContent = (item) => {
        const docRows = [
            ...(item.documents || []).map(file => ({
                name: file.file_name,
                url: file.file_url.startsWith('http') ? file.file_url : ``
            }))
        ];

        // Once a delivery is posted (od_id exists) its Final Packing List/Form is
        // always available — generated on the fly from live data, nothing
        // stored in DB.
        const hasChallan = !!item.od_id;

        if (docRows.length === 0 && !hasChallan) {
            return (
                <div style={{ textAlign: 'center', padding: '28px 0', color: '#94a3b8', fontSize: 13 }}>
                    No documents available
                </div>
            );
        }

        return (
            <div>
                {hasChallan && (
                    <div style={{ marginBottom: 16, padding: 15, border: '1px solid #d1d5db', borderRadius: 8, background: '#f8fafc' }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#000', marginBottom: 12 }}>
                            Final Packing List - {item.od_id}.pdf
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button
                                onClick={() => setShowDeliveryFormPreview(item.od_id)}
                                style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, background: '#000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                            >
                                View
                            </button>
                            <button
                                onClick={() => handleDownloadDeliveryForm(item.od_id)}
                                style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, background: '#000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                            >
                                Download
                            </button>
                            <button
                                onClick={() => handlePrintDeliveryForm(item.od_id)}
                                style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, background: '#000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                            >
                                Print
                            </button>
                        </div>
                    </div>
                )}

                {docRows.length > 0 && (
                    <div style={styles.tableContainer}>
                        <table className="table" style={styles.erpTable}>
                            <thead>
                                <tr>
                                    <th style={styles.erpTh}>#</th>
                                    <th style={styles.erpTh}>File Name</th>
                                    <th style={styles.erpTh}>Download</th>
                                </tr>
                            </thead>
                            <tbody>
                                {docRows.map((doc, idx) => (
                                    <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                        <td style={styles.erpTd}>{idx + 1}</td>
                                        <td style={styles.erpTd}>
                                            <i className="bi bi-file-earmark-pdf me-2"></i>
                                            <span style={{ color: '#1e293b', fontWeight: 500 }}>{doc.name}</span>
                                        </td>
                                        <td style={styles.erpTd}>
                                            <a href={doc.url} download className="btn btn-sm border">
                                                <i className="bi bi-download me-1"></i> Download
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    const handleSoIdClick = (so_id) => {
        navigate("/cable/sales/sales-order", {
            state: {
                openSoId: so_id,
                returnTo: location.pathname
            }
        });
    };

    const handleOpenModal = async () => {
        const firstSelectedCustomer = rfqSummary.find(item => selectedRows.includes(item.so_id))?.customer;
        const allSameCustomer = selectedRows.every(soId => rfqSummary.find(item => item.so_id === soId)?.customer === firstSelectedCustomer);
        if (!allSameCustomer) { toast.error("Please select SO IDs of the same customer."); return; }

        const selectedData = rfqSummary.filter(item => selectedRows.includes(item.so_id));
        const so_ids = selectedData.map(item => item.so_id);

        if (so_ids.length > 0) {
            try {
                const responses = await Promise.all(so_ids.map(soId =>
                    fetch(`${ORDER_API}/outbound-delivery/accepted/so/${soId}?t=${new Date().getTime()}`).then(res => res.json())
                ));
                setModalData(responses);
                setModalType('convert');
                setShowModal(true);
            } catch (error) { console.error(error); toast.error("Error fetching data"); }
        } else { toast.warning("No SO ID selected!"); }
    };

    const handleViewClick = async (so_id) => {
        try {
            const res = await fetch(`${ORDER_API}/outbound-delivery/accepted/so/${so_id}?t=${new Date().getTime()}`);
            const data = await res.json();
            setModalData([data]);
            setModalType('view');
            setViewSource('active');
            setShowModal(true);
        } catch (error) { console.error("Error fetching details:", error); }
    };

    const handlePostedViewClick = async (od_id) => {
        try {
            const res = await fetch(`${ORDER_API}/outbound-delivery/posted/od/${od_id}?t=${new Date().getTime()}`);
            const data = await res.json();
            setModalData([data]);
            setModalType('view');
            setViewSource('posted');
            setShowModal(true);
        } catch (error) { console.error("Error fetching posted delivery details:", error); }
    };

    const handleCheckboxChange = (soId) => {
        setSelectedRows((prev) => {
            if (prev.includes(soId)) return prev.filter(id => id !== soId);
            const selectedCust = rfqSummary.find(item => item.so_id === soId)?.customer;
            const firstCust = prev.length > 0 ? rfqSummary.find(item => item.so_id === prev[0])?.customer : null;
            if (firstCust && selectedCust !== firstCust) {
                toast.error("Please select SO IDs of the same customer.");
                return prev;
            }
            return [...prev, soId];
        });
    };

    const handleSelectAllChange = (checked) => {
        if (checked) {
            if (filteredData.length === 0) { toast.warning("No items found in current view."); return; }
            const firstCustomer = filteredData[0]?.customer;
            if (!filteredData.every(item => item.customer === firstCustomer)) {
                toast.error("Visible items belong to different customers. Please select manually.");
                return;
            }
            setSelectedRows(filteredData.map(item => item.so_id));
        } else {
            setSelectedRows([]);
        }
    };

    const handleCreateDelivery = () => {
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, "0");
        return `OBD${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    };

    const handleSaveDelivery = async () => {
        const generatedOdId = handleCreateDelivery();
        const allProducts = [];

        modalData.forEach(soRecord => {
            if (soRecord.productDetails) {
                soRecord.productDetails.forEach((prod, idx) => {
                    const rowKey = `${soRecord.so_id}|${prod.product_code}|${idx}`;

                    let parsedDBBatches = [];
                    if (prod.batch_number) parsedDBBatches = prod.batch_number.split('||').map(s => s.trim()).filter(Boolean);

                    const currentSelection = Array.isArray(selectedBatches[rowKey])
                        ? selectedBatches[rowKey]
                        : (selectedBatches[rowKey] ? [selectedBatches[rowKey]] : parsedDBBatches);

                    const baseProductObj = {
                        id: prod.id,
                        so_id: soRecord.so_id,
                        product_code: prod.product_code
                    };

                    // THE FIX: Push ALL batches selected from the Dropdown!
                    if (currentSelection && currentSelection.length > 0) {
                        currentSelection.forEach(batchVal => {
                            allProducts.push({
                                ...baseProductObj,
                                batch_number: batchVal
                            });
                        });
                    }
                });
            }
        });

        if (allProducts.length === 0) {
            toast.error("Please assign a batch to at least one item before saving.");
            return;
        }

        setIsSaving(true);
        const formData = { od_id: generatedOdId, productDetails: allProducts };

        try {
            const response = await fetch(`${ORDER_API}/outbound-delivery/create-delivery`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            const data = await response.json();
            if (response.ok) {
                toast.success(`Delivery ID ${generatedOdId} created successfully.`);
                setShowModal(false);
                setModalData([]);
                setIsSaving(false);
                const resSummary = await fetch(`${ORDER_API}/outbound-delivery/summary/created`);
                const summaryData = await resSummary.json();
                setRfqSummary(Array.isArray(summaryData) ? summaryData : (Array.isArray(summaryData?.data) ? summaryData.data : []));
            } else {
                toast.error(`Error: ${data.message}`);
                setIsSaving(false);
            }
        } catch (error) {
            toast.error("An error occurred.");
            setIsSaving(false);
        }
    };

    const handleSaveBatches = async () => {
        const updatesPayload = [];
        modalData.forEach(item => {
            item.productDetails?.forEach((prod, idx) => {
                const key = `${item.so_id}|${prod.product_code}|${idx}`;

                let parsedDBBatches = [];
                if (prod.batch_number) parsedDBBatches = prod.batch_number.split('||').map(s => s.trim()).filter(Boolean);

                const selectedForThisRow = Array.isArray(selectedBatches[key])
                    ? selectedBatches[key]
                    : (selectedBatches[key] ? [selectedBatches[key]] : parsedDBBatches);

                if (selectedForThisRow && selectedForThisRow.length > 0) {
                    selectedForThisRow.forEach(batchString => {
                        updatesPayload.push({ id: prod.id, so_id: item.so_id, product_code: prod.product_code, batch_number: batchString });
                    });
                }
            });
        });

        if (updatesPayload.length === 0) { toast.info("No changes to save."); return; }

        try {
            const response = await fetch(`${ORDER_API}/outbound-delivery/update-batches`, {
                method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ updates: updatesPayload }),
            });
            const result = await response.json();

            if (result.success) {
                toast.success("Batches assigned successfully.");
                const updatedModalData = modalData.map(item => ({
                    ...item,
                    productDetails: item.productDetails.map((prod, idx) => {
                        const key = `${item.so_id}|${prod.product_code}|${idx}`;
                        const newlySelected = selectedBatches[key];
                        if (newlySelected && newlySelected.length > 0) return { ...prod, batch_number: newlySelected.join('||') };
                        return prod;
                    })
                }));
                setModalData(updatedModalData);
                setSelectedBatches({});
                const res = await fetch(`${ORDER_API}/outbound-delivery/summary/created`);
                const summaryData2 = await res.json();
                setRfqSummary(Array.isArray(summaryData2) ? summaryData2 : (Array.isArray(summaryData2?.data) ? summaryData2.data : []));
            } else {
                toast.error(result.message || "Failed to update batches.");
            }
        } catch (error) { toast.error("An error occurred while saving."); }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedRows([]);
        setSelectedBatches({});
        setViewSource(null);

        if (location.state?.returnTo) {
            navigate(location.state.returnTo, {
                state: { returnParams: location.state.returnParams }
            });
        }
    };

    const isSaveDisabled = useMemo(() => {
        if (modalType === 'view' && modalData.length > 0) {
            const details = modalData[0].productDetails;
            return details && details.length > 0 && details.every(p => p.isSaved === 1);
        }
        return false;
    }, [modalData, modalType]);

    return (
        <Layout>
            <div style={styles.pageTitleBar}>
                <div>
                    <div style={styles.pageTitle}>Outbound Delivery</div>
                    <div style={styles.pageSubtitle}>Manage and view your data here</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <button className="btn btn-dark" style={styles.primaryBtn} onClick={handleOpenModal}>+ Create Delivery</button>
                <button
                        onClick={() => {
                            setManualExists(null);
                            setManualFile(null);
                            setShowManual(true);
                            fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                                .then((res) => res.json())
                                .then((data) => {
                                    const norm = (v) => String(v || "").trim().toLowerCase();
                                    const match = (data?.data || []).find((m) =>
                                        norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                                        norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                                        norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                                    );
                                    if (match && match.drive_link) {
                                        setManualFile(match);
                                        setManualExists(true);
                                    } else {
                                        setManualExists(false);
                                    }
                                })
                                .catch(() => setManualExists(false));
                        }}
    title="View User Manual"
    style={{
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: "10px",
      padding: "10px 14px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      color: "#3b82f6",
      fontSize: "14px",
      fontWeight: "600",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
  >
    <FileText size={18} />
    User Manual
  </button>
            </div>
            </div>

            {showManual && (
  <div
    onClick={() => setShowManual(false)}
    style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 1100,
    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#ffffff",
                            borderRadius: "12px",
                            width: "90vw",
                            height: "97vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                        }}
                    >
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
      }}>
        <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
          User Manual
        </h4>
        <button
          onClick={() => setShowManual(false)}
          style={{
            border: "none", background: "#f1f5f9", borderRadius: "8px",
            width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
            color: "#475569", lineHeight: 1,
          }}
          title="Close"
        >
          ×
        </button>
      </div>

      {manualExists === null && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
          Checking for user manual...
        </div>
      )}

      {manualExists === false && (
        <div style={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
        }}>
          <FileText size={40} color="#cbd5e1" />
          <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
            User Manual Not Available
          </div>
          <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
            The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
          </div>
        </div>
      )}

                        {manualExists === true && manualFile && (() => {
                            const MANUAL_SCALE = 1.25;
                            return (
                                <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                                    <iframe
                                        src={manualFile.drive_link.replace("/view", "/preview")}
                                        title={manualFile.file_name || "User Manual"}
                                        allow="autoplay"
                                        style={{
                                            border: "none",
                                            width: `${100 / MANUAL_SCALE}%`,
                                            height: `${100 / MANUAL_SCALE}%`,
                                            transform: `scale(${MANUAL_SCALE})`,
                                            transformOrigin: "top left",
                                        }}
                                    />
                                </div>
                            );
                        })()}
    </div>
  </div>
)}

            <div style={styles.filterCard}>
                <div className="row g-3">
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Sales Organization</label>
                        <Select
                            options={salesOrgOptions.map((org) => ({ value: org.code, label: org.code }))}
                            value={salesOrganization ? { value: salesOrganization, label: salesOrganization } : null}
                            onChange={(opt) => setSalesOrganization(opt ? opt.value : '')}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Customer Segment</label>
                        <Select
                            options={customerSegmentOptions.map((seg) => ({ value: seg.code, label: seg.code }))}
                            value={customerSegment ? { value: customerSegment, label: customerSegment } : null}
                            onChange={(opt) => setCustomerSegment(opt ? opt.value : '')}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Shipping From</label>
                        <Select
                            options={shippingFromOptions.map((ship) => ({ value: ship.code, label: ship.code }))}
                            value={shippingFrom ? { value: shippingFrom, label: shippingFrom } : null}
                            onChange={(opt) => setShippingFrom(opt ? opt.value : '')}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Distribution Channel</label>
                        <Select
                            options={distributionChannelOptions.map((chan) => ({ value: chan.name, label: chan.name }))}
                            value={dChannel ? { value: dChannel, label: dChannel } : null}
                            onChange={(opt) => setDChannel(opt ? opt.value : '')}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>SO ID</label>
                        <Select
                            options={dropdownOptions.soIds.map((id) => ({ value: id, label: id }))}
                            value={soId ? { value: soId, label: soId } : null}
                            onChange={(opt) => setSoId(opt ? opt.value : '')}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Customer</label>
                        <Select
                            options={dropdownOptions.customers.map((cust) => ({ value: cust, label: cust }))}
                            value={selectedCustomer ? { value: selectedCustomer, label: selectedCustomer } : null}
                            onChange={(opt) => setSelectedCustomer(opt ? opt.value : '')}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                </div>
            </div>

            {/* ===================== ACTIVE / POSTED TOGGLE ===================== */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "14px" }}>
                <div style={{
                    display: "inline-flex",
                    background: "#f1f5f9",
                    borderRadius: "8px",
                    padding: "3px",
                    gap: "2px"
                }}>
                    <button
                        onClick={() => setMainTab("active")}
                        style={{
                            border: "none",
                            borderRadius: "6px",
                            padding: "6px 18px",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: "pointer",
                            background: mainTab === "active" ? "#ffffff" : "transparent",
                            color: mainTab === "active" ? "#1e293b" : "#64748b",
                            boxShadow: mainTab === "active" ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                            transition: "all 0.15s ease"
                        }}
                    >
                        Active
                    </button>
                    <button
                        onClick={() => setMainTab("posted")}
                        style={{
                            border: "none",
                            borderRadius: "6px",
                            padding: "6px 18px",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: "pointer",
                            background: mainTab === "posted" ? "#ffffff" : "transparent",
                            color: mainTab === "posted" ? "#1e293b" : "#64748b",
                            boxShadow: mainTab === "posted" ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                            transition: "all 0.15s ease"
                        }}
                    >
                        Posted
                    </button>
                </div>
            </div>

            {mainTab === "posted" ? (
                <div style={{ ...styles.tableContainer, overflowX: 'auto' }}>
                    <table className="table" style={{ ...styles.erpTable, width: 'max-content', minWidth: '100%' }}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.erpTh, width: 140 }}>OD ID</th>
                                <th style={{ ...styles.erpTh, width: 140 }}>SO ID</th>
                                <th style={{ ...styles.erpTh, width: 160 }}>Purchase Order ID</th>
                                <th style={{ ...styles.erpTh, width: 220 }}>Customer</th>
                                <th style={{ ...styles.erpTh, width: 100 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPostedData.length === 0 ? (
                                <tr><td colSpan="5" style={{ ...styles.erpTh, textAlign: "center" }}>No Records Found</td></tr>
                            ) : (
                                filteredPostedData.map((item) => (
                                    <tr key={item.od_id}>
                                        <td style={styles.erpTd}>{item.od_id ? (
                                            <span
                                                onClick={() => handlePostedViewClick(item.od_id)}
                                                style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
                                            >
                                                {item.od_id}
                                            </span>
                                        ) : ""}</td>
                                        <td style={styles.erpTd}>{item.so_id ? (
                                            <span
                                                onClick={() => handlePostedViewClick(item.od_id)}
                                                style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
                                            >
                                                {item.so_id}
                                            </span>
                                        ) : ""}</td>
                                        <td style={styles.erpTd}>{item.purchase_order || ""}</td>
                                        <td style={styles.erpTd}>{item.customer || ""}</td>
                                        <td style={styles.erpTd}><button className="btn btn-outline-dark btn-sm" onClick={() => handlePostedViewClick(item.od_id)}>View</button></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div style={{ ...styles.tableContainer, overflowX: 'auto' }}>
                    <table className="table" style={{ ...styles.erpTable, width: 'max-content', minWidth: '100%' }}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.erpTh, width: 50, textAlign: "center" }}><input type="checkbox" checked={filteredData.length > 0 && selectedRows.length > 0 && filteredData.every(item => selectedRows.includes(item.so_id))} onChange={(e) => handleSelectAllChange(e.target.checked)} /></th>
                                <th style={{ ...styles.erpTh, width: 140 }}>SO ID</th>

                                <th style={{ ...styles.erpTh, width: 160 }}>
                                    Purchase Order ID
                                </th>

                                <th style={{ ...styles.erpTh, width: 220 }}>
                                    Customer
                                </th>

                                <th style={{ ...styles.erpTh, width: 100 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.length === 0 ? (<tr><td colSpan="5" style={{ ...styles.erpTh, textAlign: "center" }}>No Records Found</td></tr>) : (
                                filteredData.map((item) => (
                                    <tr key={item.so_id} style={{ backgroundColor: item.status === 'approved' ? "#f2f2f2" : "white", opacity: item.status === 'approved' ? 0.6 : 1 }}>
                                        <td style={{ ...styles.erpTh, width: 50, textAlign: "center" }}><input type="checkbox" checked={selectedRows.includes(item.so_id)} onChange={() => handleCheckboxChange(item.so_id)} /></td>
                                        <td style={styles.erpTd}>
                                            {item.so_id ? (
                                                <span
                                                    onClick={() => handleSoIdClick(item.so_id)}
                                                    style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
                                                >
                                                    {item.so_id}
                                                </span>
                                            ) : ""}
                                        </td>

                                        <td style={styles.erpTd}>
                                            {item.purchase_order || ""}
                                        </td>

                                        <td style={styles.erpTd}>
                                            {item.customer || ""}
                                        </td>

                                        <td style={styles.erpTd}><button className="btn btn-outline-dark btn-sm" onClick={() => handleViewClick(item.so_id)}>View</button></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && modalData.length > 0 && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "1200px", width: "90%", maxHeight: "90vh" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                {modalType === 'convert' ? 'Outbound Delivery' : viewSource === 'posted' ? (
                                    `Outbound Delivery : ${modalData[0]?.od_id || ''}`
                                ) : (
                                    <>
                                        Outbound Delivery :{" "}
                                        <span
                                            onClick={() => modalData[0]?.so_id && handleSoIdClick(modalData[0].so_id)}
                                            style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: "2px" }}
                                        >
                                            {modalData[0]?.so_id}
                                        </span>
                                    </>
                                )}
                            </div>
                            <button style={styles.iconBtn} onClick={handleCloseModal}>✕</button>
                        </div>
                        <div style={styles.modalBody}>
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <label style={styles.compactLabel}>Customer</label>
                                    <div className="form-control bg-light" style={styles.compactInput}>
                                        {modalData[0]?.customer || "—"}
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <label style={styles.compactLabel}>Shipping Address</label>
                                    <div className="form-control bg-light" style={{ ...styles.compactInput, height: 'auto', minHeight: '38px' }}>
                                        {modalData[0]?.shippingAddress || "—"}
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <label style={styles.compactLabel}>Billing Address</label>
                                    <div className="form-control bg-light" style={{ ...styles.compactInput, height: 'auto', minHeight: '38px' }}>
                                        {modalData[0]?.billingAddress || "—"}
                                    </div>
                                </div>
                            </div>
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <label style={styles.compactLabel}>Payment Terms</label>
                                    <div className="form-control bg-light" style={{ ...styles.compactInput, height: 'auto', minHeight: '38px' }}>
                                        {modalData[0]?.paymentTerms || "—"}
                                    </div>
                                </div>
                            </div>

                            <div style={styles.tabs}>
                                <button
                                    className={`btn ${activeTab === "product" ? "btn-secondary" : "btn-light"}`}
                                    style={styles.compactTabBtn}
                                    onClick={() => setActiveTab("product")}
                                >
                                    Product
                                </button>
                                <button
                                    className={`btn ${activeTab === "docs" ? "btn-secondary" : "btn-light"}`}
                                    style={styles.compactTabBtn}
                                    onClick={() => setActiveTab("docs")}
                                >
                                    Docs
                                </button>
                                <button
                                    className={`btn ${activeTab === "changeLog" ? "btn-secondary" : "btn-light"}`}
                                    style={styles.compactTabBtn}
                                    onClick={() => setActiveTab("changeLog")}
                                >
                                    Change Log
                                </button>
                            </div>

                            {activeTab === "product" && (
                                <div>
                                    {modalData.map((item) => (
                                        <div key={item.so_id} style={{ marginBottom: "15px" }}>
                                            {modalType === 'view' ? (<div style={{ padding: "10px", border: "1px solid #ddd", borderRadius: "8px", backgroundColor: "#fff" }}>{renderProductContent(item)}</div>) : (<div style={{ padding: "10px", border: "1px solid #ddd", borderRadius: "8px", backgroundColor: "#f9f9f9" }}><div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => handleToggle("product", item.so_id)}><h6 style={{ margin: 0 }}>{item.so_id}</h6><i className={`bi bi-chevron-${expandedProductSoId === item.so_id ? "down" : "right"}`} style={{ fontSize: "18px" }}></i></div>{expandedProductSoId === item.so_id && renderProductContent(item)}</div>)}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {activeTab === "docs" && (
                                <div>
                                    {modalData.map((item) => (
                                        <div key={item.so_id} style={{ marginBottom: "15px" }}>
                                            {modalType === 'view' ? (<div style={{ padding: "10px", border: "1px solid #ddd", borderRadius: "8px", backgroundColor: "#fff" }}>{renderDocsContent(item)}</div>) : (<div style={{ padding: "10px", border: "1px solid #ddd", borderRadius: "8px", backgroundColor: "#f9f9f9" }}><div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => handleToggle("docs", item.so_id)}><h6 style={{ margin: 0 }}>{item.so_id}</h6><i className={`bi bi-chevron-${expandedDocsSoId === item.so_id ? "down" : "right"}`} style={{ fontSize: "18px" }}></i></div>{expandedDocsSoId === item.so_id && renderDocsContent(item)}</div>)}
                                        </div>
                                    ))}
                                </div>
                            )}


                            {activeTab === "changeLog" && (
                                <div style={styles.tableContainer}>
                                    <table className="table" style={styles.erpTable}>
                                        <thead>
                                            <tr>
                                                <th style={styles.erpTh}>Date</th>
                                                <th style={styles.erpTh}>User</th>
                                                <th style={styles.erpTh}>Action</th>
                                                <th style={styles.erpTh}>Old Value</th>
                                                <th style={styles.erpTh}>New Value</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {changeLogs && changeLogs.length > 0 ? changeLogs.map((log, index) => (
                                                <tr key={log.id || index} style={{ background: index % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                    <td style={styles.erpTd}>
                                                        {(log.updated_at || log.created_at) ? new Date(log.updated_at || log.created_at).toLocaleString('en-IN', {
                                                            day: '2-digit', month: 'short', year: 'numeric',
                                                            hour: '2-digit', minute: '2-digit'
                                                        }) : '-'}
                                                    </td>
                                                    <td style={styles.erpTd}>{log.updated_by || log.created_by || "Unknown"}</td>
                                                    <td style={styles.erpTd}>{log.action_type || log.action || '-'}</td>
                                                    <td style={{ ...styles.erpTd, maxWidth: '150px', wordWrap: 'break-word' }}>{log.old_value || '-'}</td>
                                                    <td style={{ ...styles.erpTd, fontWeight: 600, maxWidth: '150px', wordWrap: 'break-word' }}>{log.new_value || '-'}</td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center text-muted p-3">
                                                        No change logs available
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div style={styles.modalFooter}>
                            {modalType === 'view' && viewSource !== 'posted' && (<button className="btn btn-dark" onClick={handleSaveBatches} disabled={isSaveDisabled}>{isSaveDisabled ? "Save" : "Save"}</button>)}
                            <button className="btn btn-outline-secondary" onClick={handleCloseModal}>Close</button>
                            {modalType === 'convert' && (<button className="btn btn-dark" onClick={handleSaveDelivery} disabled={isSaving}>Save</button>)}
                        </div>
                    </div>
                </div>
            )}
            {showDeliveryFormPreview && (
                <div
                    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10050, display: "flex", alignItems: "center", justifyContent: "center" }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowDeliveryFormPreview(null); }}
                >
                    <div style={{ width: "90%", maxWidth: 950, height: "90%", background: "#fff", borderRadius: "8px", position: "relative", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 15px 40px rgba(0,0,0,0.25)" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
                            <span style={{ fontWeight: "600", fontSize: "14px" }}>
                                Final Packing List - {showDeliveryFormPreview}.pdf
                            </span>
                            <button
                                onClick={() => setShowDeliveryFormPreview(null)}
                                style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b", lineHeight: 1 }}
                            >
                                ✕
                            </button>
                        </div>
                        <iframe
                            src={deliveryFormUrl(showDeliveryFormPreview)}
                            title="Final Packing List"
                            width="100%"
                            style={{ flex: 1, border: "none", borderRadius: "0 0 8px 8px" }}
                        />
                    </div>
                </div>
            )}
            <CharacteristicsModal modalVisible={showCharModal} handleClose={() => setShowCharModal(false)} selectedCharacteristics={selectedCharData} isEditing={false} />
            <ToastContainer position="top-right" autoClose={3000} />
        </Layout>
    );
}

const styles = {
    tabs: { display: 'flex', gap: 8, margin: '14px 0' },
    compactLabel: { fontSize: "12px", fontWeight: 600, marginBottom: "2px", color: '#334155' },
    compactInput: { fontSize: "13px", height: "34px", padding: "4px 8px" },
    compactTabBtn: { fontSize: "13px", padding: "4px 10px" },

    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },

    erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },

    erpTh: {
        background: "#f8fafc",
        fontSize: 12,
        fontWeight: 600,
        padding: "10px 12px",
        borderBottom: "1px solid #e5e7eb",
        color: "#1e293b",
        whiteSpace: "nowrap"
    },

    erpTd: {
        fontSize: 13,
        padding: "10px 12px",
        borderBottom: "1px solid #f1f5f9",
        color: "#334155",
        verticalAlign: "middle"
    },

    filterCard: {
        background: "#ffffff",
        borderRadius: 8,
        padding: 20,
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        marginBottom: 18
    },

    filterLabel: { fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#334155" },

    filterSelect: { height: 36, fontSize: 13 },

    pageTitleBar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 16
    },

    pageTitle: { fontSize: 20, fontWeight: 700, color: "#1e293b" },

    pageSubtitle: { fontSize: 12, color: "#64748b" },

    primaryBtn: { paddingLeft: 14, paddingRight: 14 },

    modalBackdrop: {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
        padding: "20px"
    },

    modal: {
        background: "#fff",
        borderRadius: "8px",
        width: "90%",
        maxWidth: "1200px",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
    },

    modalHeader: {
        padding: "15px 20px",
        borderBottom: "1px solid #ddd",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
    },

    iconBtn: {
        background: "transparent",
        border: "none",
        fontSize: "16px",
        cursor: "pointer"
    },

    modalBody: {
        padding: "20px",
        overflowY: "auto",
        flex: 1
    },

    modalFooter: {
        padding: "15px 20px",
        borderTop: "1px solid #ddd",
        display: "flex",
        justifyContent: "flex-end",
        gap: "10px"
    },
    modernTabs: {
        display: "flex",
        gap: "10px",
        margin: "18px 0",
        borderBottom: "1px solid #e2e8f0",
        paddingBottom: "10px"
    },

    modernTab: {
        border: "1px solid #dbeafe",
        background: "#f8fafc",
        color: "#475569",
        padding: "8px 18px",
        borderRadius: "10px",
        fontSize: "13px",
        fontWeight: "600",
        cursor: "pointer",
        transition: "0.2s ease"
    },

    activeModernTab: {
        background: "#021843",
        color: "#fff",
        border: "1px solid #021843",
        boxShadow: "0 3px 8px rgba(2,24,67,0.18)"
    }
};

export default OutboundDelivery;